import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { supabase } from "./supabaseClient";
import { useAuth } from "./AuthContext";
import { useClientTypeFilter } from "./ClientTypeFilterContext";
import { normalizeClientType } from "./clientTypes";
import type { NotificationRow } from "./notifications";

interface NotificationsContextType {
  notifications: NotificationRow[];
  loading: boolean;
  error: string | null;
  markCaseNotificationsRead: (
    caseId: string,
    type?: NotificationRow["type"]
  ) => Promise<void>;
  markNotificationsReadByIds: (notificationIds: string[]) => Promise<void>;
  /** Register a callback fired on each new signature notification. Returns unsubscribe fn. */
  onSignature: (cb: (notification: NotificationRow) => void) => () => void;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(
  undefined
);

const POLL_INTERVAL_MS = 30 * 60 * 1000; // 30 min — filet de sécurité si Realtime rate un event

// ─── Desktop notification helper ──────────────────────────────────────
function showDesktopNotification(notif: NotificationRow) {
  if (typeof window === "undefined" || !("Notification" in window)) return;
  if (Notification.permission !== "granted") return;
  // Ne pas notifier si l'onglet est actif (l'utilisateur voit déjà le badge)
  if (document.hasFocus()) return;

  const meta = notif.metadata as Record<string, string> | undefined;
  const clientName = meta?.client_name ?? "Un client";
  const caseTitle = meta?.case_title ?? "";

  if (notif.type === "message") {
    new Notification("💬 Nouveau message", {
      body: `${clientName}${caseTitle ? ` — ${caseTitle}` : ""}`,
      tag: `msg-${notif.id}`,
      icon: "/favicon.ico",
    });
  } else if (notif.type === "signature") {
    new Notification("✍️ Devis signé !", {
      body: `${clientName}${caseTitle ? ` — ${caseTitle}` : ""}`,
      tag: `sig-${notif.id}`,
      icon: "/favicon.ico",
    });
  }
}

function matchesSelectedClientTypes(
  notification: NotificationRow,
  selectedClientTypes: string[] | null
): boolean {
  if (!selectedClientTypes) return true;

  const notificationClientType = normalizeClientType(
    notification.metadata.client_type ?? null
  );

  if (!notificationClientType) return false;

  return selectedClientTypes.includes(notificationClientType);
}

function filterNotificationsByClientType(
  notifications: NotificationRow[],
  selectedClientTypes: string[] | null
): NotificationRow[] {
  return notifications.filter((notification) =>
    matchesSelectedClientTypes(notification, selectedClientTypes)
  );
}

export function NotificationsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user, loading: authLoading } = useAuth();
  const {
    selectedClientTypes,
    loading: clientTypeFilterLoading,
  } = useClientTypeFilter();
  const [notifications, setNotifications] = useState<NotificationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const channelRef = useRef<RealtimeChannel | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const signatureCallbacksRef = useRef<
    Set<(n: NotificationRow) => void>
  >(new Set());

  // Stable user ID (évite les re-renders causés par le changement de référence de l'objet user)
  const userId = user?.id ?? null;

  // ─── Demander la permission de notification desktop ─────────────────
  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      "Notification" in window &&
      Notification.permission === "default" &&
      userId
    ) {
      Notification.requestPermission();
    }
  }, [userId]);

  // ─── Fetch unread notifications ──────────────────────────────────────
  const fetchNotifications = useCallback(async (uid: string) => {
    const { data, error: fetchError } = await supabase
      .from("notifications")
      .select("id,user_id,case_id,type,created_at,read_at,metadata")
      .eq("user_id", uid)
      .is("read_at", null)
      .order("created_at", { ascending: false })
      .limit(500);

    if (fetchError) {
      setError(fetchError.message);
      return;
    }

    setNotifications(
      filterNotificationsByClientType(
        (data as NotificationRow[]) ?? [],
        selectedClientTypes
      )
    );
    setError(null);
  }, [selectedClientTypes]);

  // ─── Setup : fetch + realtime + polling ──────────────────────────────
  useEffect(() => {
    if (authLoading || clientTypeFilterLoading) return;
    if (!userId) {
      setNotifications([]);
      setLoading(false);
      setError(null);
      return;
    }

    let isCancelled = false;

    // 1. Initial fetch (une seule fois grâce au dep stable `userId`)
    const init = async () => {
      setLoading(true);
      await fetchNotifications(userId);
      if (!isCancelled) setLoading(false);
    };
    init();

    // 2. Single Realtime channel
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }

    const channel = supabase
      .channel(`notifications:${userId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          if (payload.eventType === "INSERT") {
            const newNotif = payload.new as NotificationRow;
            if (!matchesSelectedClientTypes(newNotif, selectedClientTypes)) {
              return;
            }
            setNotifications((prev) => {
              if (prev.some((n) => n.id === newNotif.id)) return prev;
              return [newNotif, ...prev];
            });
            if (newNotif.type === "signature") {
              signatureCallbacksRef.current.forEach((cb) => cb(newNotif));
            }
            showDesktopNotification(newNotif);
          }
          if (payload.eventType === "UPDATE") {
            const updated = payload.new as NotificationRow;
            if (!matchesSelectedClientTypes(updated, selectedClientTypes)) {
              setNotifications((prev) =>
                prev.filter((item) => item.id !== updated.id)
              );
              return;
            }
            if (updated.read_at) {
              setNotifications((prev) =>
                prev.filter((item) => item.id !== updated.id)
              );
            } else {
              setNotifications((prev) =>
                prev.map((item) =>
                  item.id === updated.id ? updated : item
                )
              );
            }
          }
          if (payload.eventType === "DELETE") {
            setNotifications((prev) =>
              prev.filter((item) => item.id !== payload.old.id)
            );
          }
        }
      )
      .subscribe((status, err) => {
        if (status === "CHANNEL_ERROR") {
          console.error("[Notifications] Channel error:", err);
        } else if (status === "TIMED_OUT") {
          console.error("[Notifications] Channel timeout");
        } else if (status === "SUBSCRIBED") {
          setError(null);
        }
      });

    channelRef.current = channel;

    // 3. Polling de secours (30 min)
    pollRef.current = setInterval(() => {
      fetchNotifications(userId);
    }, POLL_INTERVAL_MS);

    return () => {
      isCancelled = true;
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [userId, authLoading, clientTypeFilterLoading, fetchNotifications, selectedClientTypes]);

  // ─── Mark as read : optimistic + DB ──────────────────────────────────
  const markCaseNotificationsRead = useCallback(
    async (caseId: string, type?: NotificationRow["type"]) => {
      if (!userId) return;

      // Optimistic removal
      setNotifications((prev) =>
        prev.filter((n) => {
          if (n.case_id !== caseId) return true;
          if (type && n.type !== type) return true;
          return false;
        })
      );

      let query = supabase
        .from("notifications")
        .update({ read_at: new Date().toISOString() })
        .eq("user_id", userId)
        .eq("case_id", caseId)
        .is("read_at", null);

      if (type) {
        query = query.eq("type", type);
      }

      const { error: updateError } = await query;
      if (updateError) {
        setError(updateError.message);
        fetchNotifications(userId);
      }
    },
    [userId, fetchNotifications]
  );

  const markNotificationsReadByIds = useCallback(
    async (notificationIds: string[]) => {
      if (!userId || notificationIds.length === 0) return;

      const idSet = new Set(notificationIds);
      // Optimistic removal
      setNotifications((prev) => prev.filter((n) => !idSet.has(n.id)));

      const { error: updateError } = await supabase
        .from("notifications")
        .update({ read_at: new Date().toISOString() })
        .in("id", notificationIds)
        .eq("user_id", userId);

      if (updateError) {
        setError(updateError.message);
        fetchNotifications(userId);
      }
    },
    [userId, fetchNotifications]
  );

  // ─── Signature event callback registration ───────────────────────────
  const onSignature = useCallback(
    (cb: (notification: NotificationRow) => void) => {
      signatureCallbacksRef.current.add(cb);
      return () => {
        signatureCallbacksRef.current.delete(cb);
      };
    },
    []
  );

  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        loading,
        error,
        markCaseNotificationsRead,
        markNotificationsReadByIds,
        onSignature,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotificationsContext() {
  const context = useContext(NotificationsContext);
  if (context === undefined) {
    throw new Error(
      "useNotificationsContext doit être utilisé dans un NotificationsProvider"
    );
  }
  return context;
}
